import React, { useEffect, useState, useCallback } from 'react';
import Layout from '../components/Layout';
import api from '../api/axios';
import { C } from '../theme';

const UNITS = ['pcs','kg','gm','ltr','ml','box','bag','meter','feet','set','pair','dozen','hour','day'];

const BLANK = {
  business_id: '', name: '', sku: '', category: '', description: '',
  unit: 'pcs', price: 0, cost_price: 0, tax_rate: '',
  stock: 0, min_stock: 0, status: 'active', image_url: '',
};

export default function Products() {
  const [products,   setProducts]   = useState([]);
  const [businesses, setBusinesses] = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [bizFilter,  setBizFilter]  = useState('');   // '' = all
  const [search,     setSearch]     = useState('');
  const [catFilter,  setCatFilter]  = useState('');
  const [stFilter,   setStFilter]   = useState('');
  const [modal,      setModal]      = useState(null); // 'form'|'delete'
  const [selected,   setSelected]   = useState(null);
  const [form,       setForm]       = useState(BLANK);
  const [saving,     setSaving]     = useState(false);
  const [toast,      setToast]      = useState(null);

  const showToast = (type, text) => {
    setToast({ type, text });
    setTimeout(() => setToast(null), 3500);
  };

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [pRes, bRes] = await Promise.all([
        api.get('/api/products'),
        api.get('/api/businesses'),
      ]);
      setProducts(pRes.data);
      setBusinesses(bRes.data);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { loadAll(); }, [loadAll]);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const openAdd = () => {
    const def = businesses.find(b => b.is_default);
    setForm({ ...BLANK, business_id: def?.id || businesses[0]?.id || '' });
    setSelected(null);
    setModal('form');
  };
  const openEdit = (p) => {
    setSelected(p);
    setForm({
      business_id: p.business_id || '',
      name: p.name || '', sku: p.sku || '', category: p.category || '',
      description: p.description || '', unit: p.unit || 'pcs',
      price: p.price ?? 0, cost_price: p.cost_price ?? 0,
      tax_rate: p.tax_rate ?? '',
      stock: p.stock ?? 0, min_stock: p.min_stock ?? 0,
      status: p.status || 'active', image_url: p.image_url || '',
    });
    setModal('form');
  };
  const openDelete = (p) => { setSelected(p); setModal('delete'); };
  const close = () => { setModal(null); setSelected(null); };

  const save = async () => {
    if (!form.name?.trim())    return showToast('error', 'Product name is required.');
    if (!form.business_id)     return showToast('error', 'Select a business.');
    setSaving(true);
    try {
      const body = { ...form, tax_rate: form.tax_rate === '' ? null : Number(form.tax_rate) };
      if (selected) await api.put(`/api/products/${selected.id}`, body);
      else           await api.post('/api/products', body);
      await loadAll();
      showToast('success', selected ? 'Product updated.' : 'Product added.');
      close();
    } catch (e) {
      showToast('error', e?.response?.data?.detail || 'Save failed.');
    } finally { setSaving(false); }
  };

  const del = async () => {
    setSaving(true);
    try {
      await api.delete(`/api/products/${selected.id}`);
      await loadAll();
      showToast('success', 'Product deleted.');
      close();
    } catch (e) {
      showToast('error', e?.response?.data?.detail || 'Delete failed.');
    } finally { setSaving(false); }
  };

  const toggleStatus = async (p) => {
    await api.put(`/api/products/${p.id}`, { status: p.status === 'active' ? 'inactive' : 'active' });
    await loadAll();
  };

  // categories derived from data for filter dropdown
  const categories = [...new Set(products.map(p => p.category).filter(Boolean))].sort();

  const filtered = products.filter(p => {
    const q = search.toLowerCase();
    const matchQ = !q
      || p.name?.toLowerCase().includes(q)
      || p.sku?.toLowerCase().includes(q)
      || p.category?.toLowerCase().includes(q);
    return matchQ
      && (!bizFilter || String(p.business_id) === String(bizFilter))
      && (!catFilter || p.category === catFilter)
      && (!stFilter  || p.status  === stFilter);
  });

  const counts = {
    total:    products.length,
    active:   products.filter(p => p.status === 'active').length,
    inactive: products.filter(p => p.status !== 'active').length,
    low:      products.filter(p => p.min_stock > 0 && p.stock <= p.min_stock).length,
  };

  const bizName = (id) => businesses.find(b => b.id === id)?.name || '—';

  return (
    <Layout title="Products">

      {/* ── Toast ── */}
      {toast && (
        <div style={{
          position: 'fixed', top: '20px', right: '24px', zIndex: 3000,
          background: toast.type === 'success' ? C.successBg : C.dangerBg,
          color:      toast.type === 'success' ? C.successText : C.dangerText,
          border:     `1px solid ${toast.type === 'success' ? '#6EE7B7' : '#FCA5A5'}`,
          borderRadius: '10px', padding: '12px 20px', fontSize: '13px', fontWeight: 600,
          boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
          display: 'flex', alignItems: 'center', gap: '8px',
        }}>
          {toast.type === 'success' ? '✓' : '⚠'} {toast.text}
        </div>
      )}

      {/* ── Summary row ── */}
      <div style={{ display: 'flex', gap: '14px', marginBottom: '22px', alignItems: 'center', flexWrap: 'wrap' }}>
        {[
          { label: 'Total Products', val: counts.total,    dot: C.primary  },
          { label: 'Active',         val: counts.active,   dot: C.success  },
          { label: 'Inactive',       val: counts.inactive, dot: C.danger   },
          { label: 'Low Stock',      val: counts.low,      dot: C.warning  },
        ].map(({ label, val, dot }) => (
          <div key={label} style={{
            background: C.white, border: `1px solid ${C.border}`, borderRadius: '10px',
            padding: '14px 20px', display: 'flex', alignItems: 'center', gap: '12px',
          }}>
            <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: dot }} />
            <div>
              <div style={{ fontSize: '22px', fontWeight: 800, color: C.textMain }}>{val}</div>
              <div style={{ fontSize: '11px', color: C.textSub, fontWeight: 500 }}>{label}</div>
            </div>
          </div>
        ))}
        <button onClick={openAdd} style={{ ...btnPrimary, marginLeft: 'auto' }}>
          + Add Product
        </button>
      </div>

      {/* ── Table card ── */}
      <div style={card}>

        {/* Filters */}
        <div style={{ display: 'flex', gap: '10px', marginBottom: '18px', flexWrap: 'wrap', alignItems: 'center' }}>
          <input
            placeholder="Search name, SKU, category…"
            value={search} onChange={e => setSearch(e.target.value)}
            style={searchBox}
          />
          <select value={bizFilter} onChange={e => setBizFilter(e.target.value)} style={sel}>
            <option value="">All Businesses</option>
            {businesses.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
          </select>

          <select value={stFilter} onChange={e => setStFilter(e.target.value)} style={sel}>
            <option value="">All Status</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </select>
          {(search || bizFilter || catFilter || stFilter) && (
            <button onClick={() => { setSearch(''); setBizFilter(''); setCatFilter(''); setStFilter(''); }} style={btnGhost}>
              Clear
            </button>
          )}
          <span style={{ marginLeft: 'auto', fontSize: '12px', color: C.textSub }}>
            {filtered.length} / {products.length} products
          </span>
        </div>

        {/* Table */}
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '1000px' }}>
            <thead>
              <tr style={{ background: C.primaryBg, borderBottom: `2px solid ${C.border}` }}>
                {['Product','Business','Status','Actions'].map(h => (
                  <th key={h} style={th}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={10} style={emptyCell}>Loading products…</td></tr>
              ) : filtered.length === 0 ? (
                <tr><td colSpan={10} style={emptyCell}>
                  {products.length === 0
                    ? 'No products yet. Click "+ Add Product" to start.'
                    : 'No products match the current filters.'}
                </td></tr>
              ) : filtered.map((p, i) => (
                  <tr key={p.id} style={{ background: i % 2 === 0 ? C.white : '#FFFBF5', borderBottom: `1px solid ${C.border}` }}>
                    <td style={td}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        {p.image_url ? (
                          <img src={p.image_url} alt="" style={{ width: '38px', height: '38px', borderRadius: '8px', objectFit: 'cover', flexShrink: 0 }} />
                        ) : (
                          <div style={{
                            width: '38px', height: '38px', borderRadius: '8px', flexShrink: 0,
                            background: C.primaryLight, display: 'flex', alignItems: 'center',
                            justifyContent: 'center', fontSize: '18px',
                          }}>📦</div>
                        )}
                        <div>
                          <div style={{ fontWeight: 600, color: C.textMain, fontSize: '13px' }}>{p.name}</div>
                          {p.sku && <div style={{ fontSize: '11px', color: C.primaryDeep, fontFamily: 'monospace', fontWeight: 600 }}>{p.sku}</div>}
                          {p.description && (
                            <div style={{ fontSize: '11px', color: C.textSub, marginTop: '2px', maxWidth: '260px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {p.description}
                            </div>
                          )}
                        </div>
                      </div>
                    </td>
                    <td style={td}>
                      <span style={{
                        background: C.primaryLight, color: C.primaryDeep,
                        padding: '3px 10px', borderRadius: '20px', fontSize: '12px', fontWeight: 600,
                      }}>
                        🏢 {p.business_name || bizName(p.business_id)}
                      </span>
                    </td>
                    <td style={td}>
                      <button onClick={() => toggleStatus(p)} style={{
                        border: 'none', cursor: 'pointer', borderRadius: '20px',
                        padding: '4px 12px', fontSize: '11px', fontWeight: 600,
                        background: p.status === 'active' ? '#D1FAE5' : '#FEE2E2',
                        color:      p.status === 'active' ? '#065F46' : '#991B1B',
                      }}>
                        {p.status === 'active' ? '● Active' : '○ Inactive'}
                      </button>
                    </td>
                    <td style={td}>
                      <div style={{ display: 'flex', gap: '6px' }}>
                        <Btn label="Edit"   color="#3B82F6" onClick={() => openEdit(p)} />
                        <Btn label="Delete" color={C.danger} onClick={() => openDelete(p)} />
                      </div>
                    </td>
                  </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* ── Add / Edit Modal ── */}
      {modal === 'form' && (
        <Overlay>
          <div style={{
            background: C.white, borderRadius: '16px',
            width: '100%', maxWidth: '680px',
            maxHeight: '90vh', display: 'flex', flexDirection: 'column',
            boxShadow: '0 24px 80px rgba(0,0,0,0.3)',
            border: `1px solid ${C.border}`,
          }}>
            {/* Header */}
            <div style={{
              padding: '22px 28px', borderBottom: `1px solid ${C.border}`,
              display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0,
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <div style={{ width: '3px', height: '18px', background: C.primary, borderRadius: '2px' }} />
                <span style={{ fontSize: '16px', fontWeight: 700, color: C.textMain }}>
                  {selected ? `Edit — ${selected.name}` : 'Add New Product'}
                </span>
              </div>
              <button onClick={close} style={{ background: 'none', border: 'none', fontSize: '18px', cursor: 'pointer', color: C.textSub }}>✕</button>
            </div>

            {/* Body */}
            <div style={{ flex: 1, overflowY: 'auto', padding: '24px 28px' }}>

              {/* Business selector */}
              <Field label="Business *">
                <FSelect value={form.business_id} onChange={v => set('business_id', Number(v))}>
                  <option value="">— Select Business —</option>
                  {businesses.map(b => <option key={b.id} value={b.id}>{b.name}{b.is_default ? ' (Default)' : ''}</option>)}
                </FSelect>
              </Field>

              <Divider label="Product Details" />
              <div style={grid2}>
                <Field label="Product Name *">
                  <FInput value={form.name} onChange={v => set('name', v)} placeholder="Product Name" />
                </Field>
                <Field label="SKU (auto if blank)">
                  <FInput value={form.sku} onChange={v => set('sku', v)} placeholder="PRD-0001" />
                </Field>
              </div>
              <Field label="Description">
                <textarea value={form.description} onChange={e => set('description', e.target.value)}
                  placeholder="Short product description…" rows={3}
                  style={{ ...inputBase, resize: 'vertical', lineHeight: 1.5 }} />
              </Field>

              <Divider label="Product Image" />
              <Field label="Image URL">
                <FInput value={form.image_url} onChange={v => set('image_url', v)} placeholder="https://…" />
              </Field>
              {form.image_url && (
                <div style={{ marginTop: '-6px', marginBottom: '4px' }}>
                  <img src={form.image_url} alt="preview"
                    style={{ height: '80px', borderRadius: '8px', objectFit: 'cover', border: `1px solid ${C.border}` }}
                    onError={e => { e.target.style.display = 'none'; }} />
                </div>
              )}
            </div>

            {/* Footer */}
            <div style={{
              padding: '16px 28px', borderTop: `1px solid ${C.border}`,
              display: 'flex', justifyContent: 'flex-end', gap: '10px', flexShrink: 0,
            }}>
              <button onClick={close} style={btnGhost}>Cancel</button>
              <button onClick={save} disabled={saving} style={btnPrimary}>
                {saving ? 'Saving…' : selected ? '💾 Update Product' : '+ Add Product'}
              </button>
            </div>
          </div>
        </Overlay>
      )}

      {/* ── Delete Modal ── */}
      {modal === 'delete' && (
        <Overlay>
          <div style={{
            background: C.white, borderRadius: '14px', padding: '28px',
            width: '100%', maxWidth: '400px',
            boxShadow: '0 20px 60px rgba(0,0,0,0.25)',
          }}>
            <div style={{ fontSize: '15px', fontWeight: 700, color: C.textMain, marginBottom: '12px' }}>Delete Product</div>
            <p style={{ fontSize: '13px', color: C.textMain, lineHeight: 1.6, margin: '0 0 20px' }}>
              Delete <strong style={{ color: C.danger }}>{selected?.name}</strong>
              {' '}({selected?.sku})?<br />
              <span style={{ fontSize: '12px', color: C.textSub }}>This cannot be undone.</span>
            </p>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
              <button onClick={close} style={btnGhost}>Cancel</button>
              <button onClick={del} disabled={saving} style={btnDanger}>{saving ? 'Deleting…' : 'Delete'}</button>
            </div>
          </div>
        </Overlay>
      )}
    </Layout>
  );
}

// ── Sub-components ────────────────────────────────────────────────────────────

function Btn({ label, color, onClick }) {
  return (
    <button onClick={onClick} style={{
      padding: '4px 12px', borderRadius: '6px', fontSize: '12px', fontWeight: 600,
      background: `${color}18`, color, border: `1px solid ${color}30`, cursor: 'pointer',
    }}>{label}</button>
  );
}

function Divider({ label }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', margin: '4px 0 14px' }}>
      <span style={{ fontSize: '10px', fontWeight: 700, color: C.primaryDeep, textTransform: 'uppercase', letterSpacing: '0.8px', whiteSpace: 'nowrap' }}>
        {label}
      </span>
      <div style={{ flex: 1, height: '1px', background: C.border }} />
    </div>
  );
}

function Overlay({ children }) {
  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(28,20,7,0.65)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      zIndex: 999, padding: '20px',
    }}>{children}</div>
  );
}

function Field({ label, children }) {
  return (
    <div style={{ marginBottom: '14px' }}>
      <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: C.textMain, marginBottom: '6px' }}>
        {label}
      </label>
      {children}
    </div>
  );
}

function FInput({ type = 'text', value, onChange, placeholder }) {
  const [focus, setFocus] = useState(false);
  return (
    <input type={type} value={value ?? ''} placeholder={placeholder}
      onChange={e => onChange(e.target.value)}
      onFocus={() => setFocus(true)} onBlur={() => setFocus(false)}
      style={{ ...inputBase, borderColor: focus ? C.primary : C.border }} />
  );
}

function FSelect({ value, onChange, children }) {
  return (
    <select value={value ?? ''} onChange={e => onChange(e.target.value)}
      style={{ ...inputBase, background: C.white, cursor: 'pointer', borderColor: C.border }}>
      {children}
    </select>
  );
}

// ── Styles ────────────────────────────────────────────────────────────────────

const inputBase = {
  width: '100%', padding: '9px 12px', border: '1.5px solid', borderRadius: '8px',
  fontSize: '13px', color: C.textMain, outline: 'none',
  boxSizing: 'border-box', transition: 'border-color 0.15s',
};
const grid2 = { display: 'grid', gridTemplateColumns: '1fr 1fr',     gap: '0 18px' };
const grid3 = { display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '0 14px' };
const card      = { background: C.white, borderRadius: '12px', padding: '22px', border: `1px solid ${C.border}` };
const th        = { textAlign: 'left', padding: '11px 14px', fontSize: '11px', fontWeight: 700, color: C.textSub, textTransform: 'uppercase', letterSpacing: '0.5px' };
const td        = { padding: '12px 14px', fontSize: '13px', color: C.textMain, verticalAlign: 'middle' };
const emptyCell = { textAlign: 'center', padding: '48px', color: C.textSub, fontSize: '13px' };
const searchBox = { flex: 1, minWidth: '220px', padding: '9px 14px', border: `1.5px solid ${C.border}`, borderRadius: '8px', fontSize: '13px', outline: 'none' };
const sel       = { padding: '9px 12px', border: `1.5px solid ${C.border}`, borderRadius: '8px', fontSize: '13px', outline: 'none', background: C.white, cursor: 'pointer' };
const btnPrimary = {
  padding: '10px 22px',
  background: `linear-gradient(135deg, ${C.primary}, ${C.primaryDark})`,
  color: '#fff', border: 'none', borderRadius: '8px',
  fontSize: '13px', fontWeight: 700, cursor: 'pointer',
  boxShadow: `0 3px 10px rgba(245,166,35,0.3)`,
};
const btnGhost = {
  padding: '10px 18px', background: C.primaryBg, color: C.textSub,
  border: `1px solid ${C.border}`, borderRadius: '8px', fontSize: '13px', fontWeight: 600, cursor: 'pointer',
};
const btnDanger = {
  padding: '10px 20px', background: C.danger, color: '#fff', border: 'none',
  borderRadius: '8px', fontSize: '13px', fontWeight: 700, cursor: 'pointer',
};
